import type { ModuleConfig } from "../moduleConfig.types";
import {
  BRANDS,
  MARKETS,
  FILTERS,
  SEGMENTS,
  PERIOD_TYPE,
  PERIOD,
  POST_PERIOD_TYPE,
  POST_START_DATE,
  POST_END_DATE,
  STAT_TEST,
  BASE,
  MODE,
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  VISUALIZATION_TYPE,
  AGE_START,
  AGE_END,
  METRIC,
  RESPONSE,
  TRENDS_METRIC,
  GROUPED_BY,
  QUESTION_OPTIONS,
  VIEW_ID,
} from "@/constant/constantsThrougoutProject";

/**
 * Hierarchical screen config for the Exclusive Questions module.
 *
 * Resolution order (later layers override earlier ones):
 *   module.config
 *   → screenTypes.config
 *   → screenTypes.[name].config
 *   → screenTypes.[name].modes.config
 *   → screenTypes.[name].modes.[name].config
 *   → screenTypes.[name].modes.[name].visualisationType.config
 *   → screenTypes.[name].modes.[name].visualisationType.[name].config
 *
 * Note: Exclusive Questions is unique in that:
 *   - There is NO Market screen type
 *   - GROUPED_BY and QUESTION_OPTIONS are present in all modes for both screen types
 *   - No PROFILE visualisation type
 *   - By Segment TREND has TRENDS_METRIC and RESPONSE
 */

export const exclusiveQuestionsModuleConfig: ModuleConfig = {
  // ── Module-level base config ──────────────────────────────────────────────
  config: {
    type: "MODULE",
    module: "EXCLUSIVE_QUESTIONS",
    supportMarkets: true,
    isMarketsFilteredByBrand: false,
    minimumRequiredMarkets: 1,
    maximumAllowedMarkets: 25,
    supportBrands: true,
    isBrandsFilteredByMarket: true,
    supportSecondaryBrands: false,
    supportMode: true,
    defaultMode: "POINT_IN_TIME",
    minimumRequiredBrands: 1,
    supportBase: true,
    defaultBase: "GENPOP",
    // disableBases: ["USER", "ADRECALL", "RQ_GENPOP"]
    supportedBases: ["GENPOP", "FAMILIAR"],
    supportDatePeriod: true,
    supportVisualisationSwitch: true,
    supportedVisualisationTypes: ["BAR", "LINE", "TABLE"],
    defaultVisualisationType: "BAR",
    statTestingConfig: [
      { id: "POP", enabled: true, activeLabel: "Period-P" },
      {
        id: "YOY",
        enabled: true,
        activeLabel: "Year-Y",
        disableWhenDaysExceed: {
          days: 365,
          message:
            "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
        },
        disableForPeriods: {
          periods: ["P_W"],
          message:
            "Year-over-Year comparison is only available for Months & Quarters.",
        },
      },
      { id: "PTP", hidden: true },
    ],
  },

  screenTypes: {
    // ── By Segment ───────────────────────────────────────────────────────────
    SEGMENT: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_ONLY",
        maximumAllowedMarkets: 1,
        // Routes: [TREND, POINT_IN_TIME, PRE_POST] for By Segment
        supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
      },
      modes: {
        TREND: {
          config: {
            apiGroupedBy: "SEGMENT",
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            supportPeriod: true,
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            // By Segment TREND includes GROUPED_BY, QUESTION_OPTIONS, TRENDS_METRIC, RESPONSE
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              PERIOD,
              VISUALIZATION_TYPE,
              TRENDS_METRIC,
              RESPONSE,
              VIEW_ID,
            ],
            statTestingConfig: [
              {
                id: "POP",
                enabled: true,
                activeLabel: "Period-P",
              },
              {
                id: "YOY",
                enabled: true,
                activeLabel: "Year-Y",
                disableWhenDaysExceed: {
                  days: 365,
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
                },
                disableForPeriods: {
                  periods: ["P_W"],
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters.",
                },
                fallbackTo: "POP",
              },
              {
                id: "PTP",
                hidden: false,
                enabled: false,
                activeLabel: "Peer-P",
                disabledMessage:
                  "Peer-to-Peer comparison is only available for Point in Time analysis.",
                fallbackTo: "POP",
              },
            ],
          },
        },
        POINT_IN_TIME: {
          config: {
            apiGroupedBy: "SEGMENT",
            apiViewMode: "POINT_IN_TIME",
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
            statTestingConfig: [
              {
                id: "POP",
                enabled: true,
                activeLabel: "Period-P",
              },
              {
                id: "YOY",
                enabled: true,
                activeLabel: "Year-Y",
                disableWhenDaysExceed: {
                  days: 365,
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
                },
                disableForPeriods: {
                  periods: ["P_W"],
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters.",
                },
                fallbackTo: "POP",
              },
              { id: "PTP", enabled: true, activeLabel: "Peer-P" },
            ],
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiGroupedBy: "SEGMENT",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
            statTestingConfig: [
              {
                id: "POP",
                enabled: true,
                activeLabel: "Period-P",
              },
              {
                id: "YOY",
                enabled: true,
                activeLabel: "Year-Y",
                disableWhenDaysExceed: {
                  days: 365,
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
                },
                disableForPeriods: {
                  periods: ["P_W"],
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters.",
                },
                fallbackTo: "POP",
              },
              {
                id: "PTP",
                hidden: false,
                enabled: false,
                activeLabel: "Peer-P",
                disabledMessage:
                  "Peer-to-Peer comparison is only available for Point in Time analysis.",
                fallbackTo: "POP",
              },
            ],
          },
        },
      },
    },

    // ── By Brand ─────────────────────────────────────────────────────────────
    BRAND: {
      config: {
        type: "SCREEN_TYPE",
        segmentFilterModalMode: "SEGMENT_WITH_FILTERS",
        maximumAllowedMarkets: 1,
        // Routes: [TREND, POINT_IN_TIME, PRE_POST] for By Brand
        supportedModes: ["TREND", "POINT_IN_TIME", "PRE_POST"],
      },
      modes: {
        TREND: {
          config: {
            apiGroupedBy: "BRAND",
            apiViewMode: "TREND",
            apiSendIntervalType: true,
            apiSendFilters: true,
            supportPeriod: true,
            statTestingConfig: [
              {
                id: "POP",
                enabled: true,
                activeLabel: "Period-P",
              },
              {
                id: "YOY",
                enabled: true,
                activeLabel: "Year-Y",
                disableWhenDaysExceed: {
                  days: 365,
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
                },
                disableForPeriods: {
                  periods: ["P_W"],
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters.",
                },
                fallbackTo: "POP",
              },
              {
                id: "PTP",
                hidden: false,
                enabled: false,
                activeLabel: "Peer-P",
                disabledMessage:
                  "Peer-to-Peer comparison is only available for Point in Time analysis.",
                fallbackTo: "POP",
              },
            ],
            supportedPeriod: [
              "P_W",
              "P_M",
              "P_Q",
              "MV_1",
              "MV_2",
              "MV_4",
              "MV_8",
              "MV_12",
            ],
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              AGE_START,
              AGE_END,
              PERIOD,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
          },
        },
        POINT_IN_TIME: {
          config: {
            apiGroupedBy: "BRAND",
            apiViewMode: "POINT_IN_TIME",
            apiSendFilters: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              VISUALIZATION_TYPE,
              AGE_START,
              AGE_END,
              METRIC,
              RESPONSE,
              VIEW_ID,
            ],
            statTestingConfig: [
              {
                id: "POP",
                enabled: true,
                activeLabel: "Period-P",
              },
              {
                id: "YOY",
                enabled: true,
                activeLabel: "Year-Y",
                disableWhenDaysExceed: {
                  days: 365,
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
                },
                disableForPeriods: {
                  periods: ["P_W"],
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters.",
                },
                fallbackTo: "POP",
              },
              { id: "PTP", enabled: true, activeLabel: "Peer-P" },
            ],
          },
        },
        PRE_POST: {
          config: {
            type: "MODE",
            apiGroupedBy: "BRAND",
            apiViewMode: "PRE_POST",
            apiSendPrePeriod: true,
            apiSendFilters: true,
            supportPostDatePeriod: true,
            supportedURLKeys: [
              MARKETS,
              BRANDS,
              GROUPED_BY,
              QUESTION_OPTIONS,
              SEGMENTS,
              FILTERS,
              STAT_TEST,
              BASE,
              MODE,
              PERIOD_TYPE,
              PERIOD_START_DATE,
              PERIOD_END_DATE,
              POST_PERIOD_TYPE,
              POST_START_DATE,
              POST_END_DATE,
              AGE_START,
              AGE_END,
              VISUALIZATION_TYPE,
              VIEW_ID,
            ],
            statTestingConfig: [
              {
                id: "POP",
                enabled: true,
                activeLabel: "Period-P",
              },
              {
                id: "YOY",
                enabled: true,
                activeLabel: "Year-Y",
                disableWhenDaysExceed: {
                  days: 365,
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
                },
                disableForPeriods: {
                  periods: ["P_W"],
                  message:
                    "Year-over-Year comparison is only available for Months & Quarters.",
                },
                fallbackTo: "POP",
              },
              {
                id: "PTP",
                hidden: false,
                enabled: false,
                activeLabel: "Peer-P",
                disabledMessage:
                  "Peer-to-Peer comparison is only available for Point in Time analysis.",
                fallbackTo: "POP",
              },
            ],
          },
        },
      },
    },

    // Note: Exclusive Questions has NO Market screen type
  },
};
